import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetSessionByIdQuery } from "../../../../services";

interface SessionSummaryProps {
  themeBox: boolean;
  selectedFormatId: string;
  sessionSummaryData: any[];
  formatName: string;
  duration: number;
  formatList?: any[];
  isSessionSummary?: boolean;
}

const SessionSummary: React.FC<SessionSummaryProps> = ({
  themeBox,
  sessionSummaryData,
  formatName,
  duration,
  formatList = [],
  isSessionSummary = false,
}) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const { sessionid } = useParams<{ sessionid?: string }>();

  const { data: sessionData } = useGetSessionByIdQuery(
    sessionid!,
    { skip: !sessionid },
  );

  const durationFromSession = sessionData?.result?.duration || 0;

  const toggleSection = (id: string) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Identify all metrics present in the entire session summary to ensure consistent headers
  const allMetricNames = useMemo(() => {
    const names = new Set<string>();
    if (sessionSummaryData) {
      sessionSummaryData.forEach(block => {
        block.workouts?.forEach((w: any) => {
          w.metrics?.forEach((m: any) => {
            if (m.name !== "Sets" && m.name !== "Set") {
              names.add(m.name);
            }
          });
        });
      });
    }

    const sorted = Array.from(names);
    const order = ["Reps", "Weight", "Calorie", "Distance", "Time"];
    sorted.sort((a, b) => {
      const idxA = order.indexOf(a);
      const idxB = order.indexOf(b);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [sessionSummaryData]);

  if (!sessionSummaryData || sessionSummaryData.length === 0) {
    return (
      <div className="p-6">
        <h3 className={`text-lg font-semibold mb-4 ${themeBox ? "text-gray-200" : "text-gray-800"}`}>
          Session Summary
        </h3>
        <div className={`text-center py-12 ${themeBox ? "text-gray-400" : "text-gray-600"}`}>
          <div className="mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" className="mx-auto mb-4 opacity-50">
              <path d="M20.5 12.5C20.5 17.1944 16.6944 21 12 21C7.30558 21 3.5 17.1944 3.5 12.5C3.5 7.80558 7.30558 4 12 4C16.6944 4 20.5 7.80558 20.5 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 8V12.5L14 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h4 className="text-lg font-medium mb-2">No Session Data Available</h4>
          <p className="text-sm">No session format blocks have been created for this session yet.</p>
        </div>
      </div>
    );
  }

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Grouping logic
  const groups: { formatId: string; formatName: string; difficulty: string; blocks: any[]; duration: number }[] = [];

  if (isSessionSummary) {
    // Group by Format and Difficulty
    const formatMap = new Map<string, any>();
    formatList.forEach(f => formatMap.set(f._id, f));

    const groupedData: Record<string, any[]> = {};
    sessionSummaryData.forEach(block => {
      const key = `${block.format}_${block.difficulty}`;
      if (!groupedData[key]) groupedData[key] = [];
      groupedData[key].push(block);
    });

    // Sort groups based on the order in formatList
    const formatOrder = formatList.map(f => f._id);

    // Create groups based on format order
    formatOrder.forEach(fId => {
      const difficulties = ["BEGINNER", "ADVANCED", "GENERAL"];
      difficulties.forEach(diff => {
        const key = `${fId}_${diff}`;
        if (groupedData[key]) {
          const fmt = formatMap.get(fId);
          groups.push({
            formatId: fId,
            formatName: fmt?.name || "Format",
            difficulty: diff,
            blocks: groupedData[key],
            duration: fmt?.duration || 0
          });
        }
      });
    });
  } else {
    // Single format mode - group only by difficulty
    const difficulties = ["BEGINNER", "ADVANCED", "GENERAL"];
    difficulties.forEach(diff => {
      const blocks = sessionSummaryData.filter(b => b.difficulty === diff);
      if (blocks.length > 0) {
        groups.push({
          formatId: "single",
          formatName: formatName,
          difficulty: diff,
          blocks,
          duration: duration
        });
      }
    });
  }

  const renderTable = (blocks: any[]) => {
    return (
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full text-xs text-left border-collapse table-fixed min-w-[700px]">
          <thead>
            <tr className={`${themeBox ? "bg-[#141414] text-gray-500" : "bg-gray-50 text-gray-400"} uppercase tracking-wider`}>
              <th className="px-6 py-3 font-semibold w-[35%]">EXERCISES</th>
              <th className="px-4 py-3 font-semibold text-center w-[10%]">SET</th>
              {allMetricNames.map((m: string) => (
                <th key={m} className="px-4 py-3 font-semibold text-center whitespace-nowrap overflow-hidden text-ellipsis">
                  {m === "Weight" ? "WEIGHT (KG)" : m.toUpperCase()}
                </th>
              ))}
              <th className="px-2 py-3 font-semibold text-right w-[16%] ">BLOCK PERIOD</th>
              {/* <th className="px-6 py-3 font-semibold text-right w-[15%]">REST TIME</th> */}
            </tr>
          </thead>
          <tbody className={`${themeBox ? "text-gray-300" : "text-gray-700"}`}>
            {blocks.map((block, bIdx) => {
              const isGroup = block.type === "GROUP";
              return (
                <React.Fragment key={block._id || bIdx}>
                  {isGroup && (
                    <tr className={`${themeBox ? "bg-[#1A1A1A]/80" : "bg-gray-50/50"}`}>
                      <td colSpan={allMetricNames.length + 4} className="px-6 py-3 font-bold text-[#F3F3F6]">
                        {block.name}
                      </td>
                    </tr>
                  )}
                  {block.workouts.map((w: any, wIdx: number) => {
                    const setsMetric = w.metrics?.find((m: any) => m.name === "Sets" || m.name === "Set");
                    return (
                      <tr key={w._id || wIdx} className={`${themeBox ? "border-b border-[#2C2C2C]" : "border-b border-gray-100"} hover:bg-[#B6CB24]/5 transition-colors`}>
                        <td className={`px-6 py-4 truncate ${isGroup ? "pl-12" : ""}`} title={w.workout?.name || block.name}>
                          {w.workout?.name || block.name}
                        </td>
                        <td className="px-4 py-4 text-center text-gray-400">
                          {setsMetric?.value || block.sets || "--"}
                        </td>
                        {allMetricNames.map((mName: string) => {
                          const met = w.metrics?.find((m: any) => m.name === mName);
                          return (
                            <td key={mName} className="px-4 py-4 text-center">
                              {met?.value !== undefined && met.value !== null && met.value !== "" ? met.value : "--"}
                            </td>
                          );
                        })}
                        <td className="px-6 py-4 text-right text-gray-400 font-mono">
                          {formatTime(w.blockTiming || w.duration || block.blockTiming || block.duration || 0)}
                        </td>
                        {/* <td className="px-6 py-4 text-right text-gray-400 font-mono">
                          {formatTime(w.restPeriod || block.restTime || 0)}
                        </td> */}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };


  const totalDurationToShow = Array.from(new Set(groups.map(g => g.formatId))).reduce((acc, fId) => {
    const group = groups.find(g => g.formatId === fId);
    return acc + (group?.duration || 0);
  }, 0);

  return (
    <div className="p-4 space-y-4">
      {groups.map((group, idx) => {
        const sectionId = `${group.formatId}_${group.difficulty}`;
        const isOpen = openSections[sectionId] !== false; // Default to open

        return (
          <div key={sectionId} className={`rounded-xl overflow-hidden border ${themeBox ? "bg-[#1A1A1A] border-[#2C2C2C]" : "bg-white border-gray-200 shadow-sm"}`}>
            {/* Section Header */}
            <div
              className={`flex items-center justify-between px-6 py-4 cursor-pointer select-none ${themeBox ? "bg-[#1A1A1A]" : "bg-white"}`}
              onClick={() => toggleSection(sectionId)}
            >
              <h3 className={`text-sm font-bold ${themeBox ? "text-[#B6CB24]" : "text-lime-700"}`}>
                {group.formatName}
              </h3>

              <div className="flex items-center gap-6">
                {group.duration > 0 && (
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 21V12L15 15" stroke={themeBox ? "#F3F3F6" : "#444"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="12" r="9" stroke={themeBox ? "#F3F3F6" : "#444"} strokeWidth="1.5" />
                      <path d="M12 7V12" stroke="#B6CB24" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span className={`text-xs font-medium ${themeBox ? "text-gray-400" : "text-gray-500"}`}>Format Time</span>
                    <span className={`text-xs font-bold ${themeBox ? "text-[#F3F3F6]" : "text-gray-900"}`}>{formatTime(group.duration)}</span>
                  </div>
                )}

                <svg
                  xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  className={`transition-transform duration-300 ${isOpen ? "" : "rotate-180"}`}
                >
                  <path d="M18 15L12 9L6 15" stroke={themeBox ? "#F3F3F6" : "#444"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* Table Content */}
            <div className={`transition-all duration-300 ease-in-out ${isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}>
              <div className={`border-t ${themeBox ? "border-[#2C2C2C]" : "border-gray-100"}`}>
                {renderTable(group.blocks)}
              </div>
            </div>
          </div>
        );
      })}

      {/* Total Time Footer */}
      {totalDurationToShow > 0 && (
        <div className={`mt-8 flex items-center justify-end px-6 py-4 rounded-xl border ${themeBox ? "bg-[#141414] border-[#2C2C2C]" : "bg-gray-50 border-gray-200 shadow-inner"}`}>
          <div className="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke={themeBox ? "#F3F3F6" : "#444"} strokeWidth="1.5" />
              <path d="M12 7V12L15 15" stroke="#B6CB24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className={`text-sm font-medium ${themeBox ? "text-gray-400" : "text-gray-500"}`}>Total Time</span>
            <span className={`text-sm font-bold ${themeBox ? "text-[#B6CB24]" : "text-gray-900"}`}>{formatTime(totalDurationToShow)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionSummary;
